<template>
  <v-layout class="restaurant-tables-container">
    <v-container fluid>
      <v-card>
        <v-card-title>
          <span>{{ $t('tables') }}</span>
          <v-spacer></v-spacer>
          <!--<v-btn color="green" class="white--text mr-5"
                           :disabled="effectiveTier < 2 && tables.length >= 30"
                           v-if="hasEditPermissions"
                           @click="addTables">Carga Múltiple</v-btn>-->
          <!-- {{ $t('table.new') }} -->
          <v-btn
            v-if="isPlatformAdmin"
            icon
            class="mr-2"
            title="Configurar stickers QR"
            @click="openMenuQrStickerSettings"
          >
            <v-icon>settings</v-icon>
          </v-btn>
          <v-btn
            color="green"
            class="white--text"
            :disabled="effectiveTier < 2 && tables.length >= 30"
            v-if="hasEditPermissions"
            @click="createNewTable"
            >{{ $t('table.new') }}</v-btn
          >
        </v-card-title>
        <v-card-subtitle>
          {{ $t('tables.subtitle') }}
        </v-card-subtitle>
        <v-card-text>
          <template v-if="!tables.length">
            {{ $t('tables.empty') }}
          </template>
          <template v-else>
            <v-data-table
              sort-by="name"
              :sort-desc="false"
              :must-sort="true"
              :headers="headers"
              :items="tables"
              :items-per-page="50"
              :calculate-widths="true"
              :dense="true"
            >
              <template v-slot:item.actions="{ item }">
                <div class="children-inline">
                  <v-btn icon v-if="hasEditPermissions" @click="edit(item)"
                    ><v-icon>edit</v-icon></v-btn
                  >
                  <!--<v-btn icon
                                           @click="remove" color="red"><v-icon>delete</v-icon></v-btn>-->
                  <v-btn icon v-if="isPlatformAdmin" @click="print(item)"
                    ><v-icon>print</v-icon></v-btn
                  >
                  <v-switch
                    color="green"
                    class="ma-0"
                    v-model="item.enabled"
                    v-if="hasDisablePermissions"
                    :hide-details="true"
                    @change="changeVisible($event, item)"
                  ></v-switch>
                </div>
              </template>
            </v-data-table>
          </template>
        </v-card-text>
      </v-card>
    </v-container>
  </v-layout>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import draggable from 'vuedraggable';
  import { ITable } from '../../interfaces/ITable';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import NewTableModal from '../modals/NewTableModal.vue';
  import AddTablesModal from '../modals/AddTablesModal.vue';
  import TablesService from '../../api/services/tables.service';
  import ConfirmModal from '../modals/ConfirmModal.vue';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { IBranch } from '../../interfaces/IBranch';
  import { PermissionType } from '../../enums/PermissionType';
  import { IUser } from '../../interfaces/IUser';
  import { Globals } from '../../modules/Globals';
  import { usePrinting } from '@/composition/use-printing';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import MenuQrStickerSettingsModal from '@/components/modals/sticker/MenuQrStickerSettingsModal.vue';
  import { MenuQrStickerPrinter } from '@/lib/print/MenuQrStickerPrinter';
  import { MenuQrStickerSettings } from '@/lib/print/MenuQrStickerSettings';
  import { buildMenuQrUrl } from '@/domain/menu-qr/menu-qr-url';
  import { resolveMenuQrCopyFor } from '@/composition/menu-qr/use-menu-qr-copy';

  @Component({
    components: { draggable },
  })
  export default class RestaurantTables extends Vue {
    @Prop() restaurant: IRestaurant;
    @Prop() user: IUser;
    @Prop() branch: IBranch;
    tables: ITable[] = [];
    tablesService = new TablesService();
    headers = [];
    printing = usePrinting();
    get effectiveTier(): number {
      return getEffectiveTier(this.restaurant);
    }

    async created() {
      this.headers = [
        { text: 'ID', value: 'name', width: 200 },
        { text: this.$tc('description'), value: 'description' },
        { text: '', value: 'actions', sortable: false, width: 250 },
      ];
    }

    mounted() {
      this.init();
    }

    get hasEditPermissions() {
      return vueData.hasPermission(PermissionType.TablesManage);
    }

    get hasDisablePermissions() {
      return vueData.hasPermission(PermissionType.TablesDisable);
    }

    async init() {
      await this.loadTables();
    }

    @Watch('branch')
    onBranchChanged() {
      this.loadTables();
    }

    async changeVisible(newValue: boolean, table: ITable) {
      try {
        if (newValue) await this.tablesService.activate(table);
        else await this.tablesService.deactivate(table);
      } catch (e) {
        vueData.addAlert(
          this.$tc('errors.statusChangeFailed'),
          AlertType.Error
        );
        table.enabled = !newValue;
      }
    }

    createNewTable() {
      this.$modal.show(
        NewTableModal,
        {
          table: {
            restaurantId: this.restaurant.id,
            branchId: this.branch.id,
            name: '',
            stringId: '',
            index: this.tables.length,
          },
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadTables();
          },
        }
      );
    }

    addTables() {
      this.$modal.show(
        AddTablesModal,
        {
          table: {
            restaurantId: this.restaurant.id,
            branchId: this.branch.id,
            name: '',
            stringId: '',
            index: this.tables.length,
          },
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadTables();
          },
        }
      );
    }

    edit(table: ITable) {
      this.$modal.show(
        NewTableModal,
        {
          table: { ...table },
        },
        {
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          saved: () => {
            this.loadTables();
          },
        }
      );
    }

    openMenuQrStickerSettings() {
      this.$modal.show(
        MenuQrStickerSettingsModal,
        {
          legacyBranchId: this.branch?.id,
        },
        {
          name: 'menu-qr-sticker-settings',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 820,
          clickToClose: false,
        }
      );
    }

    getTableQrUrl(table: ITable) {
      return buildMenuQrUrl({
        restaurantId: this.restaurant.id,
        tableToken: table.token,
      });
    }

    async print(table: ITable) {
      const stickerPrinter = this.printing.stickerPrinter.value;

      if (!stickerPrinter) {
        vueData.addAlert(
          this.$t('qr.stickers.noPrinter').toString(),
          AlertType.Error
        );
        return;
      }

      if (!table.token) {
        vueData.addAlert(
          this.$t('qr.error.tableToken', { name: table.name }).toString(),
          AlertType.Error
        );
        return;
      }

      try {
        const copy = await resolveMenuQrCopyFor(this.restaurant, this.branch);
        const zpl = await MenuQrStickerPrinter.buildZpl({
          qrUrl: this.getTableQrUrl(table),
          title: table.name,
          logoUrl: this.restaurant.logo
            ? `${Globals.uploadsPath}/${this.restaurant.logo}`
            : null,
          settings: MenuQrStickerSettings.loadSettings({
            legacyBranchId: this.branch?.id,
          }),
          steps: copy.steps,
        });

        await this.printing.printZpl(stickerPrinter, zpl);
      } catch (error) {
        console.error(error);
        vueData.addAlert(
          this.$t('qr.stickers.failedOne', { title: table.name }).toString(),
          AlertType.Error
        );
      }
    }

    get isPlatformAdmin() {
      return !!this.user?.admin;
    }

    async loadTables() {
      const tables = await TablesService.getAll(
        this.restaurant.id,
        this.branch.id
      );
      this.tables = tables;
    }

    remove() {
      this.$modal.show(
        ConfirmModal,
        {
          title: this.$t('delete.title'),
          message: this.$t('delete.warning'),
        },
        {
          name: 'delete',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        }
      );
    }
  }
</script>
